import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";

interface StockLevel {
  variantId: string;
  warehouseId: string;
  quantity: number;
  reserved: number;
  reorderPoint: number;
  sku: string | null;
  productName: string | null;
  warehouseName: string | null;
  updatedAt: string;
}

interface StockResponse {
  data: StockLevel[];
  meta: { total: number; limit: number; offset: number };
}

interface Warehouse {
  id: string;
  name: string;
}

export function StockListPage() {
  const [warehouseId, setWarehouseId] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 50;

  const { data: warehouses = [] } = useQuery({
    queryKey: ["warehouses"],
    queryFn: () =>
      apiClient
        .get<{ data: Warehouse[] }>("/v1/warehouses")
        .then((r) => r.data.data),
  });

  const params: Record<string, string | number | boolean> = { limit, offset };
  if (warehouseId) params.warehouse_id = warehouseId;
  if (lowOnly) params.low = "true";

  const { data: result, isLoading } = useQuery({
    queryKey: ["stock", warehouseId, lowOnly, offset],
    queryFn: () =>
      apiClient
        .get<StockResponse>("/v1/stock", { params })
        .then((r) => r.data),
  });

  const levels: StockLevel[] = result?.data ?? [];
  const total = result?.meta?.total ?? 0;
  const totalPages = Math.ceil(total / limit);
  const page = Math.floor(offset / limit) + 1;

  function lowBadge(sl: StockLevel) {
    const isLow = sl.quantity <= sl.reorderPoint;
    return (
      <span
        className={
          isLow
            ? "rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700"
            : "rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700"
        }
      >
        {isLow ? "Sắp hết" : "OK"}
      </span>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Tồn kho</h1>

      <div className="flex flex-wrap gap-3">
        <select
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          value={warehouseId}
          onChange={(e) => {
            setWarehouseId(e.target.value);
            setOffset(0);
          }}
        >
          <option value="">Tất cả kho</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>

        <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={lowOnly}
            onChange={(e) => {
              setLowOnly(e.target.checked);
              setOffset(0);
            }}
            className="h-4 w-4 rounded border-input"
          />
          Chỉ hàng sắp hết
        </label>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Đang tải…</p>
      ) : (
        <>
          <Table>
            <THead>
              <Tr>
                <Th>SKU</Th>
                <Th>Tên sản phẩm</Th>
                <Th>Kho</Th>
                <Th className="text-right">Tồn</Th>
                <Th className="text-right">Đặt trước</Th>
                <Th className="text-right">Điểm đặt hàng</Th>
                <Th>Trạng thái</Th>
              </Tr>
            </THead>
            <TBody>
              {levels.length === 0 && (
                <Tr>
                  <Td colSpan={7} className="text-center text-muted-foreground">
                    Không có dữ liệu
                  </Td>
                </Tr>
              )}
              {levels.map((sl) => (
                <Tr key={`${sl.variantId}-${sl.warehouseId}`}>
                  <Td className="font-mono text-xs">{sl.sku ?? "—"}</Td>
                  <Td>{sl.productName ?? "—"}</Td>
                  <Td>{sl.warehouseName ?? "—"}</Td>
                  <Td className="text-right font-semibold">{sl.quantity}</Td>
                  <Td className="text-right">{sl.reserved}</Td>
                  <Td className="text-right">{sl.reorderPoint}</Td>
                  <Td>{lowBadge(sl)}</Td>
                </Tr>
              ))}
            </TBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {total} bản ghi — trang {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  Trước
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={page >= totalPages}
                >
                  Sau
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
